import { render } from 'preact';
import { CloudpackProvider } from './components/CloudpackProvider/CloudpackProvider.js';
import { Overlay } from './components/Overlay/Overlay.js';
import { ThemeProvider } from './components/ThemeProvider/ThemeProvider.js';
import { elementIds } from './constants.js';
import { createCloudpackClient, reloadCountSource, packageUpdateSource } from '@ms-cloudpack/api-server/browser';
import { handlePackageUpdate } from './handlePackageUpdate.js';

async function start(): Promise<void> {
  if (!window.__cloudpack) {
    throw new Error('Cloudpack not found on window');
  }

  const { pageSessionContext } = window.__cloudpack;

  if (!pageSessionContext) {
    throw new Error('Session context not found on window.__cloudpack');
  }

  const { apiUrl, currentSequence, sessionId, showOverlay, features } = pageSessionContext;

  // injectScripts shouldn't inject the overlay if it's disabled, but check here too
  if (showOverlay === 'never') {
    return;
  }

  const client = await createCloudpackClient({
    url: apiUrl,
    onOpen: () => {
      client.getSessionId
        .query()
        .then((newSessionId) => {
          if (sessionId === newSessionId) {
            console.log('[Cloudpack] socket opened, session Id:', sessionId);
          } else {
            console.warn('[Cloudpack] session ID mismatch, expected:', sessionId, 'but got:', newSessionId);
            // One possible reason for this is that the config has changed and the page needs to be reloaded.
            window.location.reload();
          }
        })
        .catch((err) => {
          console.error('[Cloudpack] Error getting session Id:', err);
        });
    },
  });

  // Subscribe to HMR package updates
  if (features?.hmr) {
    const { registerReactRefresh } = await import('./registerReactRefresh.js');
    registerReactRefresh();
    client.onDataChanged.subscribe(packageUpdateSource, { onData: (data) => void handlePackageUpdate(data) });
  }
  client.onDataChanged.subscribe(reloadCountSource, {
    onData: (data) => {
      if (Number(data) > currentSequence) {
        window.location.reload();
      }
    },
  });

  const rootDiv = document.createElement('div');
  rootDiv.id = elementIds.root;

  render(
    <ThemeProvider>
      <CloudpackProvider client={client}>
        <Overlay />
      </CloudpackProvider>
    </ThemeProvider>,
    rootDiv,
  );

  document.body.appendChild(rootDiv);
}

void start();
